import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { FaEye, FaCheck, FaTimes } from 'react-icons/fa';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import ImageModal from '../../components/ImageModal';

const ApprovalProcess: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  
  // Log para debugging del idioma
  console.log('🌍 ApprovalProcess - Idioma actual:', i18n.language);
  const [workers, setWorkers] = useState<any[]>([]);

  // Función para procesar certificaciones según el idioma
  const processCertifications = (certifications: any[]) => {
    if (!certifications || !Array.isArray(certifications)) return [];
    
    console.log('🔍 processCertifications - Idioma actual:', i18n.language);
    console.log('🔍 processCertifications - Certificaciones originales:', certifications);
    
    const processed = certifications.map((cert: any) => {
      const isEnglish = i18n.language === 'en';
      const result = {
        ...cert,
        name: isEnglish ? cert.name_en : cert.name_es,
        issuer: isEnglish ? cert.issuer_en : cert.issuer_es,
        description: isEnglish ? cert.description_en : cert.description_es,
        duration: isEnglish ? cert.duration_en : cert.duration_es
      };
      
      console.log('🔍 Certificación procesada:', {
        certificationId: cert.certificationId,
        original_name: cert.name,
        name_en: cert.name_en,
        name_es: cert.name_es,
        processed_name: result.name,
        isEnglish
      });
      
      return result;
    });
    
    console.log('🔍 processCertifications - Resultado final:', processed);
    return processed;
  };
  const [selected, setSelected] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{message: string, type: 'success'|'error'}|null>(null);
  const [activeTab, setActiveTab] = useState<'all'|'new'|'approved'|'pending'|'rejected'|'blocked'|'certifications-pending'>('all');
  const [reviewNotes, setReviewNotes] = useState<string>('');
  const [showReviewModal, setShowReviewModal] = useState<{show: boolean, certificationIndex?: number, action?: 'approved'|'rejected'}>({show: false});
  const [imageModal, setImageModal] = useState<{isOpen: boolean, imageUrl: string, title: string}>({isOpen: false, imageUrl: '', title: ''});
  const [certificationFilesModal, setCertificationFilesModal] = useState<{isOpen: boolean, certification: any, title: string}>({isOpen: false, certification: null, title: ''});

  useEffect(() => {
    const fetchWorkers = async () => {
      setLoading(true);
      const db = getFirestore();
      const usersSnap = await getDocs(collection(db, 'users'));
      const allWorkers: any[] = [];
      usersSnap.forEach(docu => {
        const data = docu.data();
        // Incluir usuarios con rol 'worker' o 'User' (nuevos usuarios que aún no han iniciado OnBoarding)
        if (data.role === 'worker' || data.role === 'User') {
          allWorkers.push({ id: docu.id, ...data });
        }
      });
      setWorkers(allWorkers);
      setLoading(false);
    };
    fetchWorkers();
  }, []);

  // Función para calcular el progreso del onboarding
  const getOnboardingProgress = (worker: any) => {
    const completedSteps = worker.completedSteps || [];
    const totalSteps = 6; // sector, id, address, experience, photo, certifications
    const completedCount = completedSteps.length;
    const percentage = Math.round((completedCount / totalSteps) * 100);
    
    // Verificar si hay elementos rechazados
    const hasRejections = worker.status === 'rejected' || 
                         worker.identification?.verificationStatus === 'rejected' ||
                         (worker.certifications && worker.certifications.some((cert: any) => cert['cert-status'] === 'rejected'));
    
    // Un usuario no está completo si tiene elementos rechazados, incluso si onBoarding es true
    const isComplete = worker.onBoarding === true && !hasRejections && completedCount === totalSteps;
    
    return {
      completed: completedCount,
      total: totalSteps,
      percentage,
      isComplete,
      hasRejections
    };
  };

  // Función para obtener el estado del onboarding
  const getOnboardingStatus = (worker: any) => {
    const progress = getOnboardingProgress(worker);
    
    // Si el usuario no tiene completedSteps definido y onBoarding es false, está en "Not Started"
    if (!worker.completedSteps && worker.onBoarding === false) {
      return { status: 'not-started', label: 'Not Started', color: 'bg-gray-100 text-gray-800' };
    }
    
    // Si hay elementos rechazados, mostrar como rechazado
    if (progress.hasRejections) {
      return { status: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' };
    }
    
    if (progress.isComplete) {
      return { status: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' };
    } else if (progress.completed === 0) {
      return { status: 'not-started', label: 'Not Started', color: 'bg-gray-100 text-gray-800' };
    } else if (progress.completed < progress.total) {
      return { status: 'in-progress', label: `In Progress (${progress.percentage}%)`, color: 'bg-yellow-100 text-yellow-800' };
    } else {
      return { status: 'pending', label: 'Pending Review', color: 'bg-blue-100 text-blue-800' };
    }
  };

  const handleView = async (worker: any) => {
    setSelected(worker);
    setModalOpen(true);
    
    // Marcar VerificationRead como true cuando se abre el modal
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'users', worker.id), { 
        VerificationRead: true,
        verificationReadAt: new Date().toISOString()
      });
      
      // Actualizar el estado local del worker
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === worker.id 
            ? { ...w, VerificationRead: true, verificationReadAt: new Date().toISOString() }
            : w
        )
      );
    } catch (error) {
      console.error('Error updating VerificationRead:', error);
    }
  };
  const handleClose = () => {
    setModalOpen(false);
    setSelected(null);
  };

  const handleImageClick = (imageUrl: string, title: string) => {
    console.log('Opening image modal with URL:', imageUrl, 'Title:', title);
    setImageModal({ isOpen: true, imageUrl, title });
  };

  const handleCertificationClick = (cert: any, title: string) => {
    // Verificar si hay múltiples archivos
    const files = [];
    if (cert.fileUrl) files.push({ url: cert.fileUrl, type: 'PDF', name: 'Documento PDF' });
    if (cert.frontImageUrl) files.push({ url: cert.frontImageUrl, type: 'Image', name: 'Imagen Frontal' });
    if (cert.backImageUrl) files.push({ url: cert.backImageUrl, type: 'Image', name: 'Imagen Posterior' });
    
    if (files.length === 0) {
      console.warn('Certificación sin URLs válidas:', cert);
      // Mostrar información de la certificación sin imágenes
      setImageModal({ 
        isOpen: true, 
        imageUrl: '', 
        title: `${title} - Sin archivos adjuntos` 
      });
      return;
    }
    
    if (files.length === 1) {
      // Si solo hay un archivo, mostrar directamente
      const file = files[0];
      setImageModal({ isOpen: true, imageUrl: file.url, title: `${title} (${file.name})` });
    } else {
      // Si hay múltiples archivos, mostrar la lista
      setCertificationFilesModal({ isOpen: true, certification: cert, title });
    }
  };

  const handleCloseImageModal = () => {
    setImageModal({ isOpen: false, imageUrl: '', title: '' });
  };

  const handleCloseCertificationFilesModal = () => {
    setCertificationFilesModal({ isOpen: false, certification: null, title: '' });
  };

  // Función para determinar qué se rechazó de un usuario
  const getRejectionDetails = (worker: any) => {
    const rejectionDetails: string[] = [];
    
    // Verificar si el perfil completo fue rechazado
    if (worker.status === 'rejected') {
      rejectionDetails.push('Complete Profile');
    }
    
    // Verificar si la identificación fue rechazada
    if (worker.identification?.verificationStatus === 'rejected') {
      rejectionDetails.push('ID Verification');
    }
    
    // Verificar si la foto de perfil fue rechazada
    if (worker.profilePhotoRejected) {
      rejectionDetails.push('Profile Photo');
    }
    
    // Verificar si la identificación fue rechazada específicamente
    if (worker.identificationRejected) {
      rejectionDetails.push('ID Documents');
    }
    
    // Verificar si la dirección fue rechazada
    if (worker.addressRejected) {
      rejectionDetails.push('Address');
    }
    
    // Verificar certificaciones rechazadas
    if (worker.certifications && Array.isArray(worker.certifications)) {
      const processedCerts = processCertifications(worker.certifications);
      const rejectedCerts = processedCerts.filter((cert: any) => cert['cert-status'] === 'rejected');
      if (rejectedCerts.length > 0) {
        if (rejectedCerts.length === 1) {
          rejectionDetails.push(`1 Certification: ${rejectedCerts[0].name || 'Unnamed'}`);
        } else {
          rejectionDetails.push(`${rejectedCerts.length} Certifications`);
        }
      }
    }
    
    // Verificar información faltante solo si el usuario no está completamente rechazado
    if (worker.status !== 'rejected') {
      if (!worker.identification?.frontImageUrl && !worker.identification?.backImageUrl) {
        rejectionDetails.push('Missing ID Photos');
      }
      
      if (!worker.address?.street || !worker.address?.city) {
        rejectionDetails.push('Missing Address');
      }
      
      if (!worker.certifications || worker.certifications.length === 0) {
        rejectionDetails.push('Missing Certifications');
      }
      
      if (!worker.photoURL) {
        rejectionDetails.push('Missing Profile Photo');
      }
    }
    
    // Verificar si los datos fueron eliminados por rechazo
    if (worker.identification === null) {
      rejectionDetails.push('ID Documents Removed');
    }
    
    if (worker.address === null) {
      rejectionDetails.push('Address Removed');
    }
    
    if (worker.photoURL === null) {
      rejectionDetails.push('Profile Photo Removed');
    }
    
    return rejectionDetails;
  };
  const handleApprove = async () => {
    if (!selected || !currentUser) return;
    try {
      const db = getFirestore();
      const updateData: any = { status: 'active' };
      
      // Actualizar verificationStatus de identification si existe
      if (selected.identification) {
        updateData['identification.verificationStatus'] = 'approved';
      }
      
      // Actualizar verificationStatus de address si existe
      if (selected.address) {
        updateData['address.verificationStatus'] = 'approved';
      }
      
      await updateDoc(doc(db, 'users', selected.id), updateData);
      
      // Actualizar el estado local para reflejar el cambio inmediatamente
      setSelected((prev: any | null) => {
        if (!prev) return null;
        const updated = { ...prev, status: 'active' };
        if (prev.identification) {
          updated.identification = { ...prev.identification, verificationStatus: 'approved' };
        }
        if (prev.address) {
          updated.address = { ...prev.address, verificationStatus: 'approved' };
        }
        return updated;
      });
      
      // Obtener el idioma del usuario para enviar la notificación en el idioma correcto
      const userLanguage = selected.language || 'en';
      
      // Enviar notificación al worker
      await notificationService.sendWorkerApprovedNotification(
        selected.id,
        currentUser.uid,
        reviewNotes || undefined,
        userLanguage
      );
      
      setWorkers(w => w.filter(u => u.id !== selected.id));
      setToast({
        message: t('admin.approvalProcess.verificationStatus.approved', 'User approved successfully'),
        type: 'success'
      });
      setReviewNotes('');
      handleClose();
    } catch (error) {
      console.error('Error approving worker:', error);
      setToast({
        message: 'Error al aprobar el trabajador',
        type: 'error'
      });
    }
  };

  const handleReject = async () => {
    if (!selected || !currentUser) return;
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'users', selected.id), { status: 'rejected' });
      
      // Obtener el idioma del usuario para enviar la notificación en el idioma correcto
      const userLanguage = selected.language || 'en';
      
      // Enviar notificación al worker
      await notificationService.sendWorkerRejectedNotification(
        selected.id,
        currentUser.uid,
        reviewNotes || undefined,
        userLanguage
      );
      
      setWorkers(w => w.filter(u => u.id !== selected.id));
      setToast({
        message: t('admin.approvalProcess.verificationStatus.rejected', 'User rejected'),
        type: 'error'
      });
      setReviewNotes('');
      handleClose();
    } catch (error) {
      console.error('Error rejecting worker:', error);
      setToast({
        message: 'Error al rechazar el trabajador',
        type: 'error'
      });
    }
  };

  // Función para manejar el estado de certificaciones
  const handleCertStatus = async (newStatus: 'approved' | 'rejected', certificationIndex: number) => {
    if (!currentUser || !selected) return;
    
    try {
      const db = getFirestore();
      const cert = selected.certifications[certificationIndex];
      const previousStatus = cert['cert-status'] || 'pending';
      
      let updatedCerts;
      
      if (newStatus === 'rejected') {
        // Si se rechaza, eliminar la certificación del array
        updatedCerts = selected.certifications.filter((_: any, idx: number) => idx !== certificationIndex);
      } else {
        // Si se aprueba, solo cambiar el estado
        updatedCerts = selected.certifications.map((c:any, idx:number) => 
          idx === certificationIndex ? { ...c, 'cert-status': newStatus } : c
        );
      }
      
      // Enviar notificación al worker usando certificaciones procesadas
      const processedCerts = processCertifications(selected.certifications);
      const processedCert = processedCerts[certificationIndex];
      const userLanguage = selected.language || 'en';
      
      if (newStatus === 'approved') {
        await notificationService.sendCertificationApprovedNotification(
          selected.id,
          cert.certificationId || cert.id || certificationIndex.toString(),
          processedCert.name,
          currentUser.uid,
          reviewNotes || undefined,
          userLanguage
        );
      } else {
        await notificationService.sendCertificationRejectedNotification(
          selected.id,
          cert.certificationId || cert.id || certificationIndex.toString(),
          processedCert.name,
          currentUser.uid,
          reviewNotes || undefined,
          userLanguage
        );
      }
      
      // Marcar la certificación como revisada
      const updatedCertsWithReview = updatedCerts.map((c: any, idx: number) => 
        idx === certificationIndex ? { 
          ...c, 
          'cert-status': newStatus,
          reviewedAt: new Date().toISOString(),
          reviewedBy: currentUser.uid
        } : c
      );
      
      // Actualizar con la información de revisión
      await updateDoc(doc(db, 'users', selected.id), { 
        certifications: updatedCertsWithReview,
        ...(newStatus === 'rejected' ? { onBoarding: false } : {})
      });
      
      // Actualizar el estado local
      setSelected((prev:any) => ({ 
        ...prev, 
        certifications: updatedCertsWithReview, 
        ...(newStatus === 'rejected' ? { onBoarding: false } : {}) 
      }));
      
      // Actualizar también en la lista de workers
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === selected.id 
            ? { 
                ...w, 
                certifications: updatedCertsWithReview,
                ...(newStatus === 'rejected' ? { onBoarding: false } : {})
              }
            : w
        )
      );
      
      // Registrar la revisión en el historial (opcional, no bloquear si falla)
      try {
        await notificationService.logCertificationReview({
          certificationId: cert.certificationId || cert.id || certificationIndex.toString(),
          certificationName: processedCert.name,
          previousStatus,
          newStatus,
          reviewedBy: currentUser.uid,
          reviewedAt: new Date().toISOString(),
          notes: reviewNotes || undefined,
          userId: selected.id
        });
      } catch (reviewError) {
        console.warn('Error logging certification review (non-blocking):', reviewError);
      }
      
      setToast({
        message: newStatus === 'approved' 
          ? t('common.certApproved','Certificación aprobada') 
          : t('common.certRejected','Certificación rechazada y eliminada. El usuario deberá volver a subir la certificación.'),
        type: newStatus === 'approved' ? 'success' : 'error'
      });
      setReviewNotes('');
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error updating certification status:', error);
      setToast({
        message: 'Error al actualizar el estado de la certificación',
        type: 'error'
      });
    }
  };

  // Función para rechazar foto de perfil
  const handleRejectProfilePhoto = async () => {
    if (!currentUser || !selected) return;
    
    try {
      const db = getFirestore();
      
      // Remover "photo" del array completedSteps si existe
      const currentCompletedSteps = selected.completedSteps || [];
      const updatedCompletedSteps = currentCompletedSteps.filter((step: string) => step !== 'photo');
      
      // Eliminar la foto de perfil y marcar como rechazada
      await updateDoc(doc(db, 'users', selected.id), { 
        photoURL: null, // Eliminar la foto de perfil
        onBoarding: false, // Resetear el onboarding
        profilePhotoRejected: true,
        profilePhotoRejectedAt: new Date().toISOString(),
        profilePhotoRejectedBy: currentUser.uid,
        profilePhotoRejectionNotes: reviewNotes || '',
        completedSteps: updatedCompletedSteps
      });
      
      // Actualizar el estado local
      setSelected((prev:any) => ({ 
        ...prev, 
        photoURL: null, // Eliminar la foto de perfil
        onBoarding: false, // Resetear el onboarding
        profilePhotoRejected: true,
        profilePhotoRejectedAt: new Date().toISOString(),
        profilePhotoRejectedBy: currentUser.uid,
        profilePhotoRejectionNotes: reviewNotes || '',
        completedSteps: updatedCompletedSteps
      }));
      
      // Actualizar también en la lista de workers
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === selected.id 
            ? { 
                ...w, 
                photoURL: null, // Eliminar la foto de perfil
                onBoarding: false, // Resetear el onboarding
                profilePhotoRejected: true,
                profilePhotoRejectedAt: new Date().toISOString(),
                profilePhotoRejectedBy: currentUser.uid,
                profilePhotoRejectionNotes: reviewNotes || '',
                completedSteps: updatedCompletedSteps
              }
            : w
        )
      );
      
      // Enviar notificación al worker
      const userLanguage = selected.language || 'en';
      await notificationService.sendProfilePhotoRejectedNotification(
        selected.id,
        currentUser.uid,
        reviewNotes || undefined,
        userLanguage
      );
      
      setToast({
        message: 'Foto de perfil rechazada y eliminada. El usuario deberá volver a subir una nueva foto de perfil.',
        type: 'error'
      });
      setReviewNotes('');
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error rejecting profile photo:', error);
      setToast({
        message: 'Error al rechazar la foto de perfil',
        type: 'error'
      });
      setTimeout(() => setToast(null), 2500);
    }
  };

  // Función para rechazar identificación
  const handleRejectIdentification = async () => {
    if (!currentUser || !selected) return;
    
    try {
      const db = getFirestore();
      
      // Remover "id" del array completedSteps si existe
      const currentCompletedSteps = selected.completedSteps || [];
      const updatedCompletedSteps = currentCompletedSteps.filter((step: string) => step !== 'id');
      
      // Eliminar los documentos de identificación y marcar como rechazada
      await updateDoc(doc(db, 'users', selected.id), { 
        identification: null, // Eliminar toda la información de identificación
        onBoarding: false, // Resetear el onboarding
        identificationRejected: true,
        identificationRejectedAt: new Date().toISOString(),
        identificationRejectedBy: currentUser.uid,
        identificationRejectionNotes: reviewNotes || '',
        completedSteps: updatedCompletedSteps
      });
      
      // Actualizar el estado local
      setSelected((prev:any) => ({ 
        ...prev, 
        identification: null, // Eliminar toda la información de identificación
        onBoarding: false, // Resetear el onboarding
        identificationRejected: true,
        identificationRejectedAt: new Date().toISOString(),
        identificationRejectedBy: currentUser.uid,
        identificationRejectionNotes: reviewNotes || '',
        completedSteps: updatedCompletedSteps
      }));
      
      // Actualizar también en la lista de workers
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === selected.id 
            ? { 
                ...w, 
                identification: null, // Eliminar toda la información de identificación
                onBoarding: false, // Resetear el onboarding
                identificationRejected: true,
                identificationRejectedAt: new Date().toISOString(),
                identificationRejectedBy: currentUser.uid,
                identificationRejectionNotes: reviewNotes || '',
                completedSteps: updatedCompletedSteps
              }
            : w
        )
      );
      
      // Enviar notificación al worker
      const userLanguage = selected.language || 'en';
      await notificationService.sendIdentificationRejectedNotification(
        selected.id,
        currentUser.uid,
        reviewNotes || undefined,
        userLanguage
      );
      
      setToast({
        message: 'Identificación rechazada y eliminada. El usuario deberá volver a subir nuevos documentos de identificación.',
        type: 'error'
      });
      setReviewNotes('');
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error rejecting identification:', error);
      setToast({
        message: 'Error al rechazar la identificación',
        type: 'error'
      });
      setTimeout(() => setToast(null), 2500);
    }
  };

  // Función para aprobar identificación
  const handleApproveIdentification = async () => {
    if (!currentUser || !selected || !selected.identification) return;
    
    try {
      const db = getFirestore();
      
      // Actualizar verificationStatus de identification
      await updateDoc(doc(db, 'users', selected.id), { 
        'identification.verificationStatus': 'approved'
      });
      
      // Actualizar el estado local
      setSelected((prev:any) => ({ 
        ...prev, 
        identification: { ...prev.identification, verificationStatus: 'approved' }
      }));
      
      // Actualizar también en la lista de workers
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === selected.id 
            ? { 
                ...w, 
                identification: { ...w.identification, verificationStatus: 'approved' }
              }
            : w
        )
      );
      
      setToast({
        message: 'Identificación aprobada exitosamente.',
        type: 'success'
      });
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error approving identification:', error);
      setToast({
        message: 'Error al aprobar la identificación',
        type: 'error'
      });
      setTimeout(() => setToast(null), 2500);
    }
  };

  // Función para rechazar dirección
  const handleRejectAddress = async () => {
    if (!currentUser || !selected) return;
    
    try {
      const db = getFirestore();
      
      // Remover "address" del array completedSteps si existe
      const currentCompletedSteps = selected.completedSteps || [];
      const updatedCompletedSteps = currentCompletedSteps.filter((step: string) => step !== 'address');
      
      // Eliminar la dirección y marcar como rechazada
      await updateDoc(doc(db, 'users', selected.id), { 
        address: null, // Eliminar toda la información de dirección
        onBoarding: false, // Resetear el onboarding
        addressRejected: true,
        addressRejectedAt: new Date().toISOString(),
        addressRejectedBy: currentUser.uid,
        addressRejectionNotes: reviewNotes || '',
        completedSteps: updatedCompletedSteps
      });
      
      // Actualizar el estado local
      setSelected((prev:any) => ({ 
        ...prev, 
        address: null, // Eliminar toda la información de dirección
        onBoarding: false, // Resetear el onboarding
        addressRejected: true,
        addressRejectedAt: new Date().toISOString(),
        addressRejectedBy: currentUser.uid,
        addressRejectionNotes: reviewNotes || '',
        completedSteps: updatedCompletedSteps
      }));
      
      // Actualizar también en la lista de workers
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === selected.id 
            ? { 
                ...w, 
                address: null, // Eliminar toda la información de dirección
                onBoarding: false, // Resetear el onboarding
                addressRejected: true,
                addressRejectedAt: new Date().toISOString(),
                addressRejectedBy: currentUser.uid,
                addressRejectionNotes: reviewNotes || '',
                completedSteps: updatedCompletedSteps
              }
            : w
        )
      );
      
      // Enviar notificación al worker
      const userLanguage = selected.language || 'en';
      await notificationService.sendAddressRejectedNotification(
        selected.id,
        currentUser.uid,
        reviewNotes || undefined,
        userLanguage
      );
      
      setToast({
        message: 'Dirección rechazada y eliminada. El usuario deberá volver a proporcionar una nueva dirección.',
        type: 'error'
      });
      setReviewNotes('');
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error rejecting address:', error);
      setToast({
        message: 'Error al rechazar la dirección',
        type: 'error'
      });
      setTimeout(() => setToast(null), 2500);
    }
  };

  // Función para aprobar dirección
  const handleApproveAddress = async () => {
    if (!currentUser || !selected || !selected.address) return;
    
    try {
      const db = getFirestore();
      
      // Actualizar verificationStatus de address
      await updateDoc(doc(db, 'users', selected.id), { 
        'address.verificationStatus': 'approved'
      });
      
      // Actualizar el estado local
      setSelected((prev:any) => ({ 
        ...prev, 
        address: { ...prev.address, verificationStatus: 'approved' }
      }));
      
      // Actualizar también en la lista de workers
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === selected.id 
            ? { 
                ...w, 
                address: { ...w.address, verificationStatus: 'approved' }
              }
            : w
        )
      );
      
      setToast({
        message: 'Dirección aprobada exitosamente.',
        type: 'success'
      });
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error approving address:', error);
      setToast({
        message: 'Error al aprobar la dirección',
        type: 'error'
      });
      setTimeout(() => setToast(null), 2500);
    }
  };

  // Función para desbloquear usuario
  const handleUnblockUser = async () => {
    if (!currentUser || !selected) return;
    
    try {
      const db = getFirestore();
      
      // Marcar el usuario como activo
      await updateDoc(doc(db, 'users', selected.id), { 
        status: 'active',
        unblockedAt: new Date().toISOString(),
        unblockedBy: currentUser.uid,
        blockReason: null,
        blockReasonDetails: null
      });
      
      // Actualizar el estado local
      setSelected((prev:any) => ({ 
        ...prev, 
        status: 'active',
        unblockedAt: new Date().toISOString(),
        unblockedBy: currentUser.uid,
        blockReason: null,
        blockReasonDetails: null
      }));
      
      // Actualizar también en la lista de workers
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === selected.id 
            ? { 
                ...w, 
                status: 'active',
                unblockedAt: new Date().toISOString(),
                unblockedBy: currentUser.uid,
                blockReason: null,
                blockReasonDetails: null
              }
            : w
        )
      );
      
      setToast({
        message: 'Usuario desbloqueado exitosamente. El usuario puede volver a acceder a la aplicación.',
        type: 'success'
      });
      setReviewNotes('');
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error unblocking user:', error);
      setToast({
        message: 'Error al desbloquear el usuario',
        type: 'error'
      });
      setTimeout(() => setToast(null), 2500);
    }
  };

  // Función para bloquear usuario
  const handleBlockUser = async () => {
    if (!currentUser || !selected) return;
    
    try {
      const db = getFirestore();
      
      // Marcar el usuario como bloqueado
      await updateDoc(doc(db, 'users', selected.id), { 
        status: 'blocked',
        blockedAt: new Date().toISOString(),
        blockedBy: currentUser.uid,
        blockReason: reviewNotes || '',
        blockReasonDetails: reviewNotes || ''
      });
      
      // Actualizar el estado local
      setSelected((prev:any) => ({ 
        ...prev, 
        status: 'blocked',
        blockedAt: new Date().toISOString(),
        blockedBy: currentUser.uid,
        blockReason: reviewNotes || '',
        blockReasonDetails: reviewNotes || ''
      }));
      
      // Actualizar también en la lista de workers
      setWorkers(prevWorkers => 
        prevWorkers.map(w => 
          w.id === selected.id 
            ? { 
                ...w, 
                status: 'blocked',
                blockedAt: new Date().toISOString(),
                blockedBy: currentUser.uid,
                blockReason: reviewNotes || '',
                blockReasonDetails: reviewNotes || ''
              }
            : w
        )
      );
      
      // Enviar notificación al usuario bloqueado
      const userLanguage = selected.language || 'en';
      await notificationService.sendUserBlockedNotification(
        selected.id,
        currentUser.uid,
        reviewNotes || undefined,
        userLanguage
      );
      
      setToast({
        message: 'Usuario bloqueado exitosamente. El usuario no podrá acceder a la aplicación.',
        type: 'error'
      });
      setReviewNotes('');
      setTimeout(() => setToast(null), 2500);
    } catch (error) {
      console.error('Error blocking user:', error);
      setToast({
        message: 'Error al bloquear el usuario',
        type: 'error'
      });
      setTimeout(() => setToast(null), 2500);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-red-50 via-white to-red-100 py-12 px-4 flex flex-col items-center justify-start">
      <div className="max-w-7xl w-full mx-auto">
        <h2 className="text-4xl font-extrabold text-gray-900 mb-10 tracking-tight drop-shadow-lg">
          <span className="bg-gradient-to-r from-red-500 via-pink-400 to-red-600 bg-clip-text text-transparent">
            Worker Profile Approval
          </span>
        </h2>
        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            className={`px-6 py-2 rounded-full font-bold text-lg shadow transition-all duration-200 border-2 border-red-200 ${activeTab === 'all' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-600 hover:bg-red-100'}`}
            onClick={() => setActiveTab('all')}
          >
            {t('admin.approvalProcess.tabs.all', 'All')}
          </button>
          <button
            className={`px-6 py-2 rounded-full font-bold text-lg shadow transition-all duration-200 border-2 border-blue-200 ${activeTab === 'new' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-blue-600 hover:bg-blue-100'}`}
            onClick={() => setActiveTab('new')}
          >
            {t('admin.approvalProcess.tabs.new', 'New')}
          </button>
          <button
            className={`px-6 py-2 rounded-full font-bold text-lg shadow transition-all duration-200 border-2 border-green-200 ${activeTab === 'approved' ? 'bg-green-500 text-white border-green-500' : 'bg-white text-green-600 hover:bg-green-100'}`}
            onClick={() => setActiveTab('approved')}
          >
            {t('admin.approvalProcess.tabs.approved', 'Approved')}
          </button>
          <button
            className={`px-6 py-2 rounded-full font-bold text-lg shadow transition-all duration-200 border-2 border-yellow-200 ${activeTab === 'pending' ? 'bg-yellow-400 text-white border-yellow-400' : 'bg-white text-yellow-700 hover:bg-yellow-100'}`}
            onClick={() => setActiveTab('pending')}
          >
            {t('admin.approvalProcess.tabs.pending', 'Pending')}
          </button>
          <button
            className={`px-6 py-2 rounded-full font-bold text-lg shadow transition-all duration-200 border-2 border-gray-300 ${activeTab === 'rejected' ? 'bg-gray-500 text-white border-gray-500' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            onClick={() => setActiveTab('rejected')}
          >
            {t('admin.approvalProcess.tabs.rejected', 'Rejected')}
          </button>
          <button
            className={`px-6 py-2 rounded-full font-bold text-lg shadow transition-all duration-200 border-2 border-red-300 ${activeTab === 'blocked' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 hover:bg-red-100'}`}
            onClick={() => setActiveTab('blocked')}
          >
            {t('admin.approvalProcess.tabs.blocked', 'Blocked')}
          </button>
          <button
            className={`px-6 py-2 rounded-full font-bold text-lg shadow transition-all duration-200 border-2 border-purple-300 ${activeTab === 'certifications-pending' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-700 hover:bg-purple-100'}`}
            onClick={() => setActiveTab('certifications-pending')}
          >
            {t('admin.approvalProcess.tabs.certificationsPending', 'Certifications Pending')}
          </button>
        </div>
        {/* Tabla filtrada */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600"></div>
            <span className="ml-6 text-xl text-gray-600 font-semibold animate-pulse">Loading...</span>
          </div>
        ) : (
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/40">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-red-100 via-white to-red-50">
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">Name</th>
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">Email</th>
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">Registration Date</th>
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">OnBoarding Status</th>
                    <th className="px-6 py-5 text-left text-lg font-extrabold text-gray-900 border-b-2 border-gray-200 sticky top-0 z-10">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {workers
                    .filter(worker => {
                      if (activeTab === 'all') return true; // Mostrar todos los workers en el tab "All"
                      if (activeTab === 'new') {
                        // Mostrar usuarios que no tienen VerificationRead: true
                        return worker.VerificationRead !== true;
                      }
                      if (activeTab === 'approved') {
                        return (
                          worker.identification?.verificationStatus === 'approved' ||
                          worker.status === 'active'
                        );
                      }
                      if (activeTab === 'pending') return worker.identification?.verificationStatus === 'pending';
                      if (activeTab === 'rejected') {
                        return (
                          worker.identification?.verificationStatus === 'rejected' ||
                          worker.status === 'rejected'
                        );
                      }
                      if (activeTab === 'blocked') {
                        return worker.status === 'blocked';
                      }
                      if (activeTab === 'certifications-pending') {
                        // Mostrar usuarios que tienen certificaciones pendientes de revisión
                        return worker.certifications && 
                               Array.isArray(worker.certifications) && 
                               worker.certifications.some((cert: any) => 
                                 !cert['cert-status'] || cert['cert-status'] === 'pending'
                               );
                      }
                      return true;
                    })
                    .map((worker, index) => (
                      <tr key={worker.id} className={index % 2 === 0 ? 'bg-white/60' : 'bg-red-50/60 hover:bg-red-100/80 transition-colors duration-200'}>
                        <td className="px-6 py-5 text-gray-900 border-b border-gray-200 flex items-center gap-4">
                          <div className="relative group">
                            <div className="w-12 h-12 rounded-full border-4 border-red-200 group-hover:border-red-500 transition-all duration-300 shadow-lg overflow-hidden">
                              <img src={worker.photoURL || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(worker.firstName + ' ' + worker.lastName)} alt="avatar" className="w-full h-full object-cover" />
                            </div>
                            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-xs bg-white/80 px-2 py-0.5 rounded-xl shadow text-red-500 font-bold opacity-0 group-hover:opacity-100 transition-all duration-300">
                              {worker.status}
                            </span>
                            {worker.status === 'blocked' && (
                              <div className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-1 shadow-lg">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-semibold text-lg">{worker.firstName} {worker.lastName}</span>
                            {worker.status === 'blocked' && (
                              <span className="text-xs text-red-600 font-semibold bg-red-100 px-2 py-1 rounded-full">
                                🔒 BLOQUEADO
                              </span>
                            )}
                            {worker.certifications && 
                             Array.isArray(worker.certifications) && 
                             worker.certifications.some((cert: any) => !cert['cert-status'] || cert['cert-status'] === 'pending') && (
                              <span className="text-xs text-purple-600 font-semibold bg-purple-100 px-2 py-1 rounded-full">
                                📜 CERTIFICACIONES PENDIENTES
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5 text-gray-900 border-b border-gray-200">
                          {worker.email}
                        </td>
                        <td className="px-6 py-5 text-gray-900 border-b border-gray-200">
                          {(() => {
                            if (!worker.createdAt) return '-';
                            if (typeof worker.createdAt === 'object' && typeof worker.createdAt.toDate === 'function') {
                              return worker.createdAt.toDate().toLocaleString();
                            }
                            const date = new Date(worker.createdAt);
                            if (!isNaN(date.getTime())) {
                              return date.toLocaleString();
                            }
                            return String(worker.createdAt);
                          })()}
                        </td>
                        <td className="px-6 py-5 border-b border-gray-200">
                          {(() => {
                            const onboardingStatus = getOnboardingStatus(worker);
                            const progress = getOnboardingProgress(worker);
                            
                            return (
                              <div className="flex flex-col gap-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${onboardingStatus.color}`}>
                                  {onboardingStatus.label}
                                </span>
                                {progress.completed > 0 && (
                                  <div className="flex items-center gap-2">
                                    <div className="w-20 bg-gray-200 rounded-full h-2">
                                      <div 
                                        className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                                        style={{ width: `${progress.percentage}%` }}
                                      ></div>
                                    </div>
                                    <span className="text-xs text-gray-600">{progress.completed}/{progress.total}</span>
                                  </div>
                                )}
                                {progress.completed > 0 && worker.completedSteps && (
                                  <div className="text-xs text-gray-500">
                                    Steps: {worker.completedSteps.join(', ')}
                                  </div>
                                )}
                                {progress.completed === 0 && (
                                  <div className="text-xs text-gray-500">
                                    No steps completed yet
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-5 border-b border-gray-200">
                          <button 
                            onClick={() => handleView(worker)}
                            className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-pink-500 hover:to-red-500 text-white border-none py-3 px-7 rounded-xl text-lg font-bold shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
                          >
                            <FaEye/>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {modalOpen && selected && (
          <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 animate-fadeIn">
            <div className="bg-white rounded-3xl p-12 w-11/12 max-w-4xl max-h-[92vh] overflow-y-auto relative shadow-2xl border border-white/40 animate-fadeInUp">
              <button 
                onClick={handleClose}
                className="absolute top-4 right-4 bg-white/70 border border-red-200 text-red-500 hover:bg-red-500 hover:text-white cursor-pointer p-2 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 text-xl"
              >
                <FaTimes/>
              </button>
              <h2 className="text-red-600 font-extrabold text-3xl mb-8 tracking-tight drop-shadow-lg">
                Profile Review
              </h2>
              <div className="flex gap-10 items-start mb-8 flex-wrap">
                <div className="relative group">
                  {selected.photoURL ? (
                    <>
                      <div className="w-36 h-36 rounded-full border-8 border-red-200 group-hover:border-red-500 transition-all duration-300 shadow-2xl overflow-hidden">
                        <img src={selected.photoURL} alt="Foto de perfil" className="w-full h-full object-cover" />
                      </div>
                      <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-sm bg-white/80 px-3 py-1 rounded-xl shadow text-red-500 font-bold opacity-0 group-hover:opacity-100 transition-all duration-300">
                        {selected.status}
                      </span>
                      {/* Botón de rechazar foto de perfil */}
                      <button
                        onClick={handleRejectProfilePhoto}
                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition-all duration-300 transform hover:scale-110"
                        title={t('admin.approvalProcess.rejectProfilePhoto', 'Rechazar foto de perfil')}
                      >
                        <FaTimes className="text-sm" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="w-36 h-36 rounded-full border-8 border-red-200 bg-red-100 flex items-center justify-center shadow-2xl">
                        <div className="text-red-500 text-4xl">📷</div>
                      </div>
                      <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-sm bg-white/80 px-3 py-1 rounded-xl shadow text-red-500 font-bold opacity-0 group-hover:opacity-100 transition-all duration-300">
                        {selected.status}
                      </span>
                      <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full px-2 py-1 text-xs font-bold">
                        ❌
                      </div>
                      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 text-xs text-red-600 font-semibold bg-white/90 px-2 py-1 rounded">
                        Foto eliminada
                      </div>
                    </>
                  )}
                </div>
                <div className="flex flex-col gap-3 text-lg">
                  <div><span className="font-bold">Name:</span> {selected.firstName} {selected.lastName}</div>
                  <div><span className="font-bold">Email:</span> {selected.email}</div>
                  <div><span className="font-bold">Verification Status:</span> 
                    <span className={`inline-block px-4 py-1 rounded-xl text-base font-semibold text-white ml-2 ${
                      selected.identification?.verificationStatus === 'approved' ? 'bg-green-500' : 
                      selected.identification?.verificationStatus === 'rejected' ? 'bg-gray-500' : 'bg-red-600'
                    }`}>
                      {selected.identification?.verificationStatus === 'approved'
                        ? 'Approved'
                        : selected.identification?.verificationStatus === 'rejected'
                          ? 'Rejected'
                          : selected.identification?.verificationStatus === 'pending'
                            ? 'Pending'
                            : (selected.identification?.verificationStatus || 'N/A')
                      }
                    </span>
                  </div>
                  <div><span className="font-bold">OnBoarding:</span> {selected.onBoarding ? '✔️' : '❌'}</div>
                  <div><span className="font-bold">OnBoarding Progress:</span> 
                    {(() => {
                      const progress = getOnboardingProgress(selected);
                      const status = getOnboardingStatus(selected);
                      return (
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${status.color}`}>
                            {status.label}
                          </span>
                          <div className="flex items-center gap-1">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${progress.percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-600">{progress.completed}/{progress.total}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {(() => {
                    // Si el usuario tiene elementos rechazados, mostrar detalles de rechazo
                    const progress = getOnboardingProgress(selected);
                    if (progress.hasRejections) {
                      const rejectionDetails = getRejectionDetails(selected);
                      return (
                        <div>
                          <span className="font-bold text-red-600">{t('admin.approvalProcess.rejectionDetails', 'Rejection Details')}:</span> 
                          <div className="mt-1 text-sm">
                            {rejectionDetails.length > 0 ? (
                              rejectionDetails.map((detail: string, index: number) => (
                                <span key={index} className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded mr-1 mb-1 text-xs font-semibold">
                                  ❌ {detail}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-500 italic">No specific rejection details available</span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    
                    // Si no está rechazado, mostrar steps completados como antes
                    if (selected.completedSteps && selected.completedSteps.length > 0) {
                      return (
                        <div><span className="font-bold">Completed Steps:</span> 
                          <div className="mt-1 text-sm text-gray-600">
                            {selected.completedSteps.map((step: string, index: number) => (
                              <span key={index} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded mr-1 mb-1 text-xs">
                                {step}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div><span className="font-bold">Completed Steps:</span> 
                          <div className="mt-1 text-sm text-gray-500 italic">
                            No steps completed yet
                          </div>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>
              <div className="mb-7 grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Dirección */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-red-600 text-2xl font-bold tracking-tight">Address</h3>
                    {selected.address && (
                      <div className="flex gap-2">
                        {selected.address.verificationStatus !== 'approved' && (
                          <button
                            onClick={handleApproveAddress}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                            title={t('admin.approvalProcess.approveAddress', 'Aprobar dirección')}
                          >
                            <FaCheck className="text-sm" />
                            {t('admin.approvalProcess.approveAddress', 'Aprobar Dirección')}
                          </button>
                        )}
                        <button
                          onClick={handleRejectAddress}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                          title={t('admin.approvalProcess.rejectAddress', 'Rechazar dirección')}
                        >
                          <FaTimes className="text-sm" />
                          {t('admin.approvalProcess.rejectAddress', 'Rechazar Dirección')}
                        </button>
                      </div>
                    )}
                  </div>
                  {selected.address ? (
                    <div className="text-gray-900">
                      <div>{selected.address?.street} {selected.address?.number}, {selected.address?.apartment}</div>
                      <div>{selected.address?.city}, {selected.address?.state}, {selected.address?.country}, {selected.address?.zipCode}</div>
                      <div>{selected.address?.additionalInfo}</div>
                    </div>
                  ) : (
                    <div className="text-red-500 italic font-semibold">
                      ❌ Dirección eliminada por rechazo
                    </div>
                  )}
                </div>
                {/* Identificación */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-red-600 text-2xl font-bold tracking-tight">Identification</h3>
                    {(selected.identification?.frontImageUrl || selected.identification?.backImageUrl) && (
                      <div className="flex gap-2">
                        {selected.identification.verificationStatus !== 'approved' && (
                          <button
                            onClick={handleApproveIdentification}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                            title={t('admin.approvalProcess.approveIdentification', 'Aprobar identificación')}
                          >
                            <FaCheck className="text-sm" />
                            {t('admin.approvalProcess.approveIdentification', 'Aprobar ID')}
                          </button>
                        )}
                        <button
                          onClick={handleRejectIdentification}
                          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                          title={t('admin.approvalProcess.rejectIdentification', 'Rechazar identificación')}
                        >
                          <FaTimes className="text-sm" />
                          {t('admin.approvalProcess.rejectIdentification', 'Rechazar ID')}
                        </button>
                      </div>
                    )}
                  </div>
                  {selected.identification ? (
                    <>
                      <div className="text-gray-900 mb-3">
                        <div><span className="font-bold">Type:</span> {selected.identification?.type}</div>
                        <div><span className="font-bold">Number:</span> {selected.identification?.number}</div>
                      </div>
                      <div className="flex gap-4 flex-wrap">
                        {selected.identification?.frontImageUrl && (
                          <img 
                            src={selected.identification.frontImageUrl} 
                            alt="ID Front" 
                            className="w-32 rounded-lg shadow-lg border-2 border-red-200 hover:border-red-500 transition-all duration-300 cursor-pointer transform hover:scale-105"
                            onClick={() => handleImageClick(selected.identification.frontImageUrl, 'ID Front')}
                          />
                        )}
                        {selected.identification?.backImageUrl && (
                          <img 
                            src={selected.identification.backImageUrl} 
                            alt="ID Back" 
                            className="w-32 rounded-lg shadow-lg border-2 border-red-200 hover:border-red-500 transition-all duration-300 cursor-pointer transform hover:scale-105"
                            onClick={() => handleImageClick(selected.identification.backImageUrl, 'ID Back')}
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-red-500 italic font-semibold">
                      ❌ Documentos de identificación eliminados por rechazo
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-7">
                <h3 className="text-red-600 text-2xl font-bold mb-3 tracking-tight">Certifications</h3>
                {selected.certifications && selected.certifications.length > 0 ? (
                  <div className="flex flex-col gap-4 w-full">
                    {processCertifications(selected.certifications).map((cert:any, i:number) => {
                      // Determinar color según cert-status
                      const status = cert['cert-status'] || 'pending';
                      let cardColor = 'bg-yellow-100 border-yellow-300'; // Pending por defecto
                      if (status === 'approved') cardColor = 'bg-green-100 border-green-300';
                      if (status === 'rejected') cardColor = 'bg-red-100 border-red-300';

                      // Verificar si es una certificación nueva (sin estado definido)
                      const isNewCertification = !cert['cert-status'];
                      
                      return (
                        <div key={i} className={`w-full rounded-2xl border shadow-md px-6 py-5 grid grid-cols-1 md:grid-cols-[2.5fr,0.5fr] gap-4 items-center relative transition-all duration-300 ${cardColor}`}>
                          {/* Indicador de certificación nueva */}
                          {isNewCertification && (
                            <div className="absolute -top-2 -left-2 bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                              🆕 NUEVA
                            </div>
                          )}
                          {/* Columna izquierda: datos de la certificación */}
                          <div className="flex flex-col gap-2">
                            <div className="font-bold text-lg text-gray-900">{cert.name}</div>
                            <div className="text-gray-700 font-normal">({cert.issuer})</div>
                            <div className="text-gray-500 text-sm">{t('workerDashboard.date')}: {cert.date}</div>
                            {/* Aquí puedes agregar más campos si existen */}
                            {cert.description && <div className="text-gray-600 text-sm">{cert.description}</div>}
                          </div>
                          {/* Columna derecha: grid 3x2 para estado y acciones */}
                          <div className="grid grid-cols-3 grid-rows-2 gap-2 w-full items-center justify-items-end">
                            {/* Fila 1: Estado (ocupa las 3 columnas) */}
                            <div className={`col-span-3 px-3 py-1 rounded-xl text-xs font-semibold text-center ${status === 'approved' ? 'bg-green-500 text-white' : status === 'rejected' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'}`}>{status === 'approved' ? t('workerDashboard.approved') : status === 'rejected' ? t('workerDashboard.rejected') : t('workerDashboard.pending')}</div>
                            {/* Fila 2: Ver, Aprobar, Negar */}
                            <div className="flex justify-end">
                              <button 
                                onClick={() => handleCertificationClick(cert, `Certificado: ${cert.name}`)}
                                className="text-red-600 hover:text-red-800 text-2xl transition-colors" 
                                title={t('admin.approvalProcess.viewCertificate','Ver Certificado')}
                              >
                                <FaEye />
                              </button>
                            </div>
                            <div className="flex justify-end">
                              <button 
                                onClick={() => {
                                  setShowReviewModal({show: true, certificationIndex: i, action: 'approved'});
                                }} 
                                className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow transition-all" 
                                title={t('admin.approvalProcess.approve')}
                              >
                                <FaCheck />
                              </button>
                            </div>
                            <div className="flex justify-end">
                              <button 
                                onClick={() => {
                                  setShowReviewModal({show: true, certificationIndex: i, action: 'rejected'});
                                }} 
                                className="bg-black hover:bg-gray-900 text-white rounded-full p-2 shadow transition-all" 
                                title={t('admin.approvalProcess.reject')}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-gray-500 italic">No certifications</div>
                )}
              </div>
              {/* Campo de notas de revisión */}
              <div className="mb-6">
                <label htmlFor="reviewNotes" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('notifications.reviewNotesOptional')}
                </label>
                <textarea
                  id="reviewNotes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={t('notifications.addReviewComments')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>
              
              <div className="flex gap-8 justify-end mt-10">
                <button 
                  onClick={handleApprove}
                  className="bg-red-600 hover:bg-red-700 text-white border-none py-4 px-10 rounded-2xl text-xl font-bold shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-3"
                >
                  <FaCheck/>
                  {t('notifications.approve')}
                </button>
                <button 
                  onClick={handleReject}
                  className="bg-black hover:bg-gray-900 text-white border-none py-4 px-10 rounded-2xl text-xl font-bold shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-3"
                >
                  <FaTimes/>
                  {t('notifications.reject')}
                </button>
                {selected.status === 'blocked' ? (
                  <button 
                    onClick={handleUnblockUser}
                    className="bg-green-600 hover:bg-green-700 text-white border-none py-4 px-10 rounded-2xl text-xl font-bold shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-3"
                  >
                    <FaCheck/>
                    {t('admin.approvalProcess.unblockUser', 'Desbloquear Usuario')}
                  </button>
                ) : (
                  <button 
                    onClick={handleBlockUser}
                    className="bg-red-800 hover:bg-red-900 text-white border-none py-4 px-10 rounded-2xl text-xl font-bold shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-3"
                  >
                    <FaTimes/>
                    {t('admin.approvalProcess.blockUser', 'Bloquear Usuario')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de revisión de certificaciones */}
        {showReviewModal.show && showReviewModal.certificationIndex !== undefined && selected && (
          <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 animate-fadeIn">
            <div className="bg-white rounded-3xl p-8 w-11/12 max-w-2xl max-h-[80vh] overflow-y-auto relative shadow-2xl border border-white/40 animate-fadeInUp">
              <button 
                onClick={() => setShowReviewModal({show: false})}
                className="absolute top-4 right-4 bg-white/70 border border-red-200 text-red-500 hover:bg-red-500 hover:text-white cursor-pointer p-2 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 text-xl"
              >
                <FaTimes/>
              </button>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                {showReviewModal.action === 'approved' ? t('notifications.approve') : t('notifications.reject')} {t('admin.approvalProcess.certification')}
              </h3>
              
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                  Certification: {processCertifications(selected.certifications)[showReviewModal.certificationIndex]?.name}
                </h4>
                <p className="text-gray-600">
                                      Issuer: {processCertifications(selected.certifications)[showReviewModal.certificationIndex]?.issuer}
                </p>
              </div>
              
              <div className="mb-6">
                <label htmlFor="certificationReviewNotes" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('notifications.reviewNotes')} {showReviewModal.action === 'rejected' ? t('notifications.reviewNotesRecommended') : t('notifications.reviewNotesOptional')}
                </label>
                <textarea
                  id="certificationReviewNotes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={showReviewModal.action === 'rejected' ? 
                    t('notifications.explainRejectionReason') : 
                    t('notifications.addReviewComments')
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={4}
                />
              </div>
              
              <div className="flex gap-4 justify-end">
                <button 
                  onClick={() => setShowReviewModal({show: false})}
                  className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-xl font-semibold transition-colors duration-200"
                >
                  {t('notifications.cancel')}
                </button>
                <button 
                  onClick={async () => {
                    if (showReviewModal.action && showReviewModal.certificationIndex !== undefined) {
                      await handleCertStatus(showReviewModal.action, showReviewModal.certificationIndex);
                      setShowReviewModal({show: false});
                    }
                  }}
                  className={`px-6 py-3 text-white rounded-xl font-semibold transition-colors duration-200 ${
                    showReviewModal.action === 'approved' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {showReviewModal.action === 'approved' ? t('notifications.approve') : t('notifications.reject')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast de alerta */}
        {toast && (
          <div className={`fixed top-6 right-6 z-[9999] px-6 py-4 rounded-xl shadow-lg text-white text-lg font-semibold transition-all duration-500 animate-fadeIn ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
            {toast.message}
          </div>
        )}

        {/* Modal de imagen */}
        <ImageModal
          isOpen={imageModal.isOpen}
          onClose={handleCloseImageModal}
          imageUrl={imageModal.imageUrl}
          title={imageModal.title}
        />

        {/* Modal de archivos de certificación */}
        {certificationFilesModal.isOpen && certificationFilesModal.certification && (
          <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 animate-fadeIn">
            <div className="bg-white rounded-3xl p-8 w-11/12 max-w-2xl max-h-[80vh] overflow-y-auto relative shadow-2xl border border-white/40 animate-fadeInUp">
              <button 
                onClick={handleCloseCertificationFilesModal}
                className="absolute top-4 right-4 bg-white/70 border border-red-200 text-red-500 hover:bg-red-500 hover:text-white cursor-pointer p-2 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 text-xl"
              >
                <FaTimes/>
              </button>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                {certificationFilesModal.title} - Archivos
              </h3>
              
              <div className="space-y-4">
                {certificationFilesModal.certification.fileUrl && (
                  <div 
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setImageModal({ 
                        isOpen: true, 
                        imageUrl: certificationFilesModal.certification.fileUrl, 
                        title: `${certificationFilesModal.title} (PDF)` 
                      });
                      handleCloseCertificationFilesModal();
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">Documento PDF</h4>
                        <p className="text-sm text-gray-600">Archivo PDF de la certificación</p>
                      </div>
                      <FaEye className="text-gray-400 text-xl" />
                    </div>
                  </div>
                )}
                
                {certificationFilesModal.certification.frontImageUrl && (
                  <div 
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setImageModal({ 
                        isOpen: true, 
                        imageUrl: certificationFilesModal.certification.frontImageUrl, 
                        title: `${certificationFilesModal.title} (Imagen Frontal)` 
                      });
                      handleCloseCertificationFilesModal();
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">Imagen Frontal</h4>
                        <p className="text-sm text-gray-600">Vista frontal de la certificación</p>
                      </div>
                      <FaEye className="text-gray-400 text-xl" />
                    </div>
                  </div>
                )}
                
                {certificationFilesModal.certification.backImageUrl && (
                  <div 
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setImageModal({ 
                        isOpen: true, 
                        imageUrl: certificationFilesModal.certification.backImageUrl, 
                        title: `${certificationFilesModal.title} (Imagen Posterior)` 
                      });
                      handleCloseCertificationFilesModal();
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">Imagen Posterior</h4>
                        <p className="text-sm text-gray-600">Vista posterior de la certificación</p>
                      </div>
                      <FaEye className="text-gray-400 text-xl" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalProcess; 